import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeftRight } from 'lucide-react';
import { InvestmentAccount } from '@/hooks/useInvestments';

interface PortfolioSummaryCardProps {
  totalValue: number;
  currency: string;
  investmentsByType: Record<string, InvestmentAccount[]>;
  isLoading: boolean;
  onCurrencyToggle?: () => void;
  title?: string;
}

/**
 * PortfolioSummaryCard component displays aggregated portfolio metrics including
 * total value, investment counts, and breakdown by type.
 * 
 * @param totalValue - Total portfolio value in selected currency
 * @param currency - The selected currency for display
 * @param investmentsByType - Investments grouped by type ID
 * @param isLoading - Loading state indicator
 * @param onCurrencyToggle - Optional callback to toggle currency
 * @param title - Optional title of the card
 * 
 * @example
 * <PortfolioSummaryCard
 *   totalValue={50000}
 *   currency="USD"
 *   investmentsByType={groupedInvestments}
 *   isLoading={false}
 *   onCurrencyToggle={handleToggle}
 * />
 */
export function PortfolioSummaryCard({
  totalValue,
  currency,
  investmentsByType,
  isLoading,
  onCurrencyToggle,
  title = 'Portfolio'
}: PortfolioSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title} Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const typeCount = Object.keys(investmentsByType).length;
  const totalInvestments = Object.values(investmentsByType).reduce(
    (sum, investments) => sum + investments.length,
    0
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle>{title} Summary</CardTitle>
        {onCurrencyToggle && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCurrencyToggle}
            className="gap-2"
            aria-label={`Change currency from ${currency}`}
          >
            <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
            {currency}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-muted-foreground">Total {title} Value</p>
          <p className="text-3xl font-bold">
            {currency} {totalValue.toFixed(2)}
          </p>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Total {title}</p>
            <p className="text-2xl font-semibold">{totalInvestments}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Investment Types</p>
            <p className="text-2xl font-semibold">{typeCount}</p>
          </div>
        </div>

        {typeCount > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">By Type</p>
            <div className="space-y-1">
              {Object.entries(investmentsByType).map(([typeId, investments]) => (
                <div key={typeId} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {investments[0]?.investment_type?.name || 'Unknown'}
                  </span>
                  <span className="font-medium">{investments.length}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
